import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAddToCart } from '@quizzach/api-hooks';
import { AuthenticationModal, useToast } from '@quizzach/components';
import { useAuth, useQuizOwnershipContext } from '@quizzach/contexts';
import { IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import { IcBasket, IcCheck } from '@quizzach/assets';
import { BtnSpinner, QuickAddBtn } from '@quizzach/styles';

/**
 * Corner quick-add circle shared by the ticket cards and the featured izlog:
 * adds to cart (guests get the auth modal) or, when the quiz is owned, leads
 * to the profile collection with a checkmark icon.
 */
export const QuickAddButton = ({ quiz }: { quiz: IPremadeQuiz }) => {
	const { t } = useTranslation();
	const { error, loading, addToCart } = useAddToCart({
		type: 'premade',
		uuid: quiz.uuid,
	});

	const { isQuizOwned } = useQuizOwnershipContext();
	const isOwned = isQuizOwned(quiz);
	const { isAuthenticated } = useAuth();
	const { showToast: showCartToast } = useToast();
	const [showModal, setShowModal] = useState(false);

	const [showCheckmark, setShowCheckmark] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const performAdd = async () => {
		const { success } = await addToCart();

		if (!success) {
			showCartToast({
				severity: 'error',
				message: t('toast.problem'),
				description: t('toast.notAddedText'),
				actionLabel: t('common.retry'),
				onAction: () => {
					void performAdd();
				},
			});
		} else {
			setShowCheckmark(true);
			showCartToast({
				severity: 'success',
				message: t('toast.addedToCart'),
				description: quiz.title,
				actionLabel: t('toast.cart'),
				onAction: () => navigate(RoutePath.Summary),
			});
		}
	};

	const handleAddToBasket = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (loading) return;
		if (!isAuthenticated) {
			setShowModal(true);
		} else {
			void performAdd();
		}
	};

	const handlePlayOwnedQuiz = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		navigate(`/profile`);
	};

	return (
		<>
			{showModal && (
				<AuthenticationModal
					closeModal={() => setShowModal(false)}
					path={location.pathname}
				/>
			)}

			{isOwned ? (
				<QuickAddBtn
					type='button'
					aria-label={t('nav.myCollection')}
					onClick={handlePlayOwnedQuiz}
				>
					<IcCheck size={18} />
				</QuickAddBtn>
			) : (
				<QuickAddBtn
					type='button'
					aria-label={t('premade.single.addToCart')}
					aria-busy={loading}
					onClick={handleAddToBasket}
				>
					{loading ? (
						<BtnSpinner />
					) : showCheckmark && !error ? (
						<IcCheck size={18} />
					) : (
						<IcBasket size={19} />
					)}
				</QuickAddBtn>
			)}
		</>
	);
};
